import { buttonVariants } from '@/app/common/components/button/buttonVariants'
import DottedCircle from '@/app/common/components/DottedCircle'
import IconBox from '@/app/common/components/IconBox'
import { AppRoutePaths } from '@/routing/route_paths'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shad/ui/dialog'
import { Check, CircleOff } from 'lucide-react'
import { Link } from 'react-router-dom'
import { MARKETPLACE_STATUS_MAP } from '../ProductForm/constants'

type Props = {
  open: boolean
  providerId: number
  marketplaceStatus: string
}

export default function ProductAddedDialog({
  open,
  providerId,
  marketplaceStatus,
}: Props) {
  const IS_INACTIVE = marketplaceStatus === MARKETPLACE_STATUS_MAP.INACTIVE

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent showClose={false} className="sm:max-w-[520px] p-0">
        <div className="p-5">
          <DialogHeader className="mb-2">
            <DialogTitle className="text-xl">
              Product added Successfully
            </DialogTitle>
          </DialogHeader>

          <p className="text-sm text-gray-600">
            We've received your product and it's now being reviewed. Here's what
            happens next:
          </p>

          {IS_INACTIVE ? (
            <div className="mt-4 space-y-4">
              <div className="flex items-start gap-3">
                <IconBox className="flex-shrink-0">
                  <DottedCircle state="active" />
                </IconBox>

                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-medium">Admin Review</div>
                    <span className="ml-auto inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-medium text-orange-700">
                      In&nbsp;Progress
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    Our product team is reviewing your product for quality and
                    compliance.
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <IconBox size={'lg'}>
                  <CircleOff className="h-5 w-5 text-gray-600" />
                </IconBox>
                <div className="flex-1">
                  <div className="font-medium">Product is Inactive</div>
                  <div className="text-sm text-gray-600">
                    Your product is inactive and won't show up in the store
                    preview or the live store.
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-4 space-y-4">
              <div className="flex items-start gap-3">
                <IconBox size={'lg'}>
                  <Check className="h-5 w-5 text-green-600" />
                </IconBox>
                <div className="flex-1">
                  <div className="font-medium">Product Submitted</div>
                  <div className="text-sm text-gray-600">
                    Your product details have been successfully received.
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <IconBox className="flex-shrink-0">
                  <DottedCircle state="active" />
                </IconBox>

                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-medium">Admin Review</div>
                    <span className="ml-auto inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-medium text-orange-700">
                      In&nbsp;Progress
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    Our product team is reviewing your product for quality and
                    compliance.
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <IconBox>
                  <DottedCircle />
                </IconBox>
                <div className="flex-1">
                  <div className="font-medium">Marketplace Preview</div>
                  <div className="text-sm text-gray-600">
                    We'll generate a preview to show how your store will appear
                    to customers.
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <IconBox>
                  <DottedCircle />
                </IconBox>
                <div className="flex-1">
                  <div className="font-medium">Your Final Review</div>
                  <div className="text-sm text-gray-600">
                    You'll review and approve any changes before going live.
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <IconBox>
                  <DottedCircle />
                </IconBox>
                <div className="flex-1">
                  <div className="font-medium">Go Live!</div>
                  <div className="text-sm text-gray-600">
                    Your product will be published and available to customers.
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-10">
            <Link
              className={buttonVariants({
                variant: 'primary',
                className: 'w-full',
                size: 'lg',
              })}
              to={AppRoutePaths.providers.provider_details.marketplace.replace(
                ':providerId',
                providerId.toString()
              )}
            >
              Okay
            </Link>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
